import { FC, useState } from 'react';
import { FolderOpen, Home } from 'lucide-react';
import { ParcImmobilierMinimalDto, RealEstateDto } from 'api/real-estate';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { Loading } from 'components';
import { BiensList } from './BiensList';
import { ParcsList } from './ParcsList';
import { ParcDetail } from './ParcDetail';
import { BienDetailDrawer } from './BienDetailDrawer';
import { UsePersonBiensResult } from './usePersonBiens';

type PersonParcProps = UsePersonBiensResult;

type SubTab = 'biens' | 'parcs';

/**
 * Onglet "Biens immobiliers" de la fiche client :
 * - sous-onglet "Biens" : recherche parmi TOUS les biens (serveur + pagination),
 * - sous-onglet "Parcs" : recherche parmi les parcs → détail d'un parc (ses biens).
 * Le détail d'un bien s'ouvre en drawer depuis les deux sous-onglets.
 * Les données viennent de usePersonBiens, instancié dans PersonContent.
 */
export const PersonParc: FC<PersonParcProps> = ({
  biens,
  totalCount,
  initialTotal,
  parcs,
  loading,
  page,
  pageSize,
  setPage,
  search,
  setSearch,
  error
}) => {
  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.Common]);

  const [subTab, setSubTab] = useState<SubTab>('biens');
  const [selectedParc, setSelectedParc] = useState<ParcImmobilierMinimalDto | null>(null);
  const [selectedBien, setSelectedBien] = useState<RealEstateDto | null>(null);

  if (loading && biens.length === 0 && !search) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border min-h-64 relative">
        <Loading loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  const showSubTabs = parcs.length > 0;

  return (
    <div className="space-y-4">
      {/* Sous-onglets Biens / Parcs (affichés uniquement si le client a des parcs) */}
      {showSubTabs && (
        <div className="bg-white rounded-xl p-2 shadow-sm border inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSubTab('biens')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
              subTab === 'biens'
                ? 'bg-primary text-white'
                : 'text-gray-600 hover:bg-primary/5'
            }`}
          >
            <Home className="w-4 h-4" />
            Biens
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                subTab === 'biens' ? 'bg-white/20' : 'bg-primary/10 text-primary'
              }`}
            >
              {initialTotal ?? 0}
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setSubTab('parcs');
              setSelectedParc(null);
            }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
              subTab === 'parcs'
                ? 'bg-primary text-white'
                : 'text-gray-600 hover:bg-primary/5'
            }`}
          >
            <FolderOpen className="w-4 h-4" />
            Parcs
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                subTab === 'parcs' ? 'bg-white/20' : 'bg-primary/10 text-primary'
              }`}
            >
              {parcs.length}
            </span>
          </button>
        </div>
      )}

      {/* Contenu */}
      {subTab === 'biens' || !showSubTabs ? (
        <BiensList
          biens={biens}
          totalCount={totalCount}
          loading={loading}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          search={search}
          setSearch={setSearch}
          onSelectBien={setSelectedBien}
          t={t}
        />
      ) : selectedParc ? (
        <ParcDetail
          parc={selectedParc}
          onBack={() => setSelectedParc(null)}
          onSelectBien={setSelectedBien}
          t={t}
        />
      ) : (
        <ParcsList parcs={parcs} onSelectParc={setSelectedParc} />
      )}

      {/* Drawer détail bien (commun aux deux sous-onglets) */}
      <BienDetailDrawer bien={selectedBien} onClose={() => setSelectedBien(null)} t={t} />
    </div>
  );
};
